import type { AppStrings } from './types.js';
import { plural } from './plural.js';

export const nl: AppStrings = {
  // COMMON
  error_title: 'Fout',
  cancel: 'Annuleren',
  retry: 'Opnieuw proberen',
  back_arrow: '← Terug',

  // ASK SCREEN
  ask_box_unavailable: 'Box niet beschikbaar',
  ask_box_unavailable_desc: 'De link is mogelijk verlopen of de eigenaar heeft de box gesloten.',
  ask_create_own_box: 'Maak je eigen box',
  ask_sent_title: 'Verzonden ✅',
  ask_sent_desc: 'Je anonieme vraag is afgeleverd bij de box-eigenaar.',
  ask_anonymous_hint: 'Je vraag wordt anoniem verzonden.',
  ask_placeholder: 'Schrijf je vraag…',
  ask_send: 'Versturen',
  ask_sending: 'Versturen…',
  ask_error_rate_limit: 'Te veel verzoeken — wacht even een minuutje.',
  ask_error_restart: 'Start de Mini App opnieuw op.',
  ask_error_load: 'Box kon niet worden geladen.',
  ask_error_send: 'Vraag kon niet worden verzonden.',
  ask_error_banned: 'Je bent geblokkeerd in de app.',
  ask_banned_appeal_btn: 'Bezwaar maken',
  ask_appeal_sent: 'Bezwaar ingediend. We bekijken het handmatig.',

  // AGE GATE
  age_gate_title: 'Alleen 18+',
  age_gate_desc:
    'Je stuurt een anonieme vraag. Wees respectvol: pesterijen, bedreigingen, seksuele inhoud met minderjarigen en inhoud die de wet overtreedt zijn verboden. Anonimiteit betekent geen straffeloosheid — overtredingen worden gemeld aan de moderatie. Door een vraag te sturen bevestig je dat je 18+ bent en de regels accepteert.',
  age_gate_confirm: 'Ik ben 18+ — doorgaan',

  // INBOX SCREEN
  inbox_title: 'Jouw box',
  box_link_accordion: 'Uitnodigingslink',
  inbox_notifications_ask: '🔔 Mag de bot je meldingen sturen over nieuwe vragen?',
  inbox_notifications_allow: 'Toestaan',
  inbox_notifications_dismiss_label: 'Sluiten',
  inbox_box_toggle_disable: 'Vragen uitschakelen',
  inbox_box_toggle_enable: 'Vragen inschakelen',
  inbox_box_disabled_warning: 'Vragen zijn uitgeschakeld — er worden geen nieuwe vragen geaccepteerd.',
  inbox_share_hint: 'Deel de link zodat vrienden je anonieme vragen kunnen stellen.',
  inbox_copy: 'Kopiëren',
  inbox_copied: 'Gekopieerd',
  inbox_copy_error: 'Fout',
  inbox_no_deeplink: 'Link verschijnt na de deploy.',
  inbox_invite_story: 'Uitnodigen via Story',
  inbox_invite_chat: 'Uitnodigen via chat',
  inbox_card_rendering: 'Uitnodigingskaart wordt nog aangemaakt.',
  inbox_story_unsupported:
    'Stories zijn niet beschikbaar in deze versie van Telegram. Probeer "via chat".',
  inbox_story_error: 'Story kon niet worden geopend.',
  inbox_chat_unsupported:
    'Chat-delen is niet beschikbaar in deze versie van Telegram. Update je client.',
  inbox_chat_error: 'Delen mislukt. Probeer het opnieuw.',
  inbox_chat_prepare_error: 'Uitnodiging kon niet worden voorbereid.',
  inbox_filter_incoming: 'Binnenkomend',
  inbox_filter_hidden: 'Verborgen',
  inbox_filter_blocked: 'Geblokkeerd',
  inbox_filter_empty: 'Hier is nog niets.',
  inbox_acc_unanswered: 'Onbeantwoord',
  inbox_acc_answered: 'Beantwoord',
  inbox_acc_hidden: 'Verborgen',
  inbox_acc_blocked: 'Geblokkeerd',
  inbox_empty: 'Nog geen vragen. Deel de link hierboven.',
  inbox_load_more: 'Meer laden',
  inbox_load_more_error: 'Volgende pagina kon niet worden geladen.',
  inbox_error_load: 'Box kon niet worden geladen.',
  inbox_rate_limit: 'Te veel verzoeken — wacht even een minuutje.',
  inbox_toggle_error: 'Box-status kon niet worden gewijzigd.',

  // MESSAGE BOX (Трек D)
  message_tagline: 'Schrijf me anoniem',
  message_placeholder: 'Schrijf een bericht…',
  message_anonymous_hint: 'Je bericht wordt anoniem verzonden.',
  message_sent_title: 'Verzonden ✅',
  message_sent_desc: 'Je anonieme bericht is afgeleverd.',
  message_owner_entry: 'Schrijf me',
  message_share_hint: 'Deel de link zodat iedereen je een anoniem bericht kan sturen.',
  message_inbox_open: 'Ontvangen berichten',
  message_inbox_title: 'Berichten',
  message_inbox_empty: 'Nog geen berichten. Deel je link hierboven.',

  // ANSWER SCREEN
  answer_title: 'Vraag beantwoorden',
  answer_placeholder: 'Schrijf je antwoord…',
  answer_submit: 'Versturen',
  answer_submitting: 'Versturen…',
  answer_back: 'Terug',
  answer_error_send: 'Antwoord kon niet worden verzonden.',

  // SHARE SCREEN
  share_title: 'Antwoord gepubliceerd ✅',
  share_hint: 'Deel de kaart en ontvang nog meer vragen.',
  share_card_rendering_desc:
    'De kaart wordt nog aangemaakt. Ga terug naar vragen en kijk straks even — dan kun je hem in een Story of chat delen.',
  share_card_alt: 'Antwoordkaart',
  share_story_btn: 'Delen via Story',
  share_story_btn_busy: 'Openen…',
  share_chat_btn: 'Delen via chat',
  share_chat_btn_busy: 'Voorbereiden…',
  share_back: 'Terug naar vragen',
  share_story_unsupported:
    'Stories zijn niet beschikbaar in deze versie van Telegram. Probeer "Delen via chat".',
  share_story_error: 'Story kon niet worden geopend. Probeer "Delen via chat".',
  share_chat_sent: 'Bericht verzonden.',
  share_chat_unsupported:
    'Chat-delen is niet beschikbaar in deze versie van Telegram. Update je client.',
  share_chat_error: 'Delen mislukt. Probeer het opnieuw.',
  share_chat_prepare_error: 'Bericht kon niet worden voorbereid.',

  // LEADERBOARD SCREEN
  lb_title: 'Ranglijst',
  lb_period_7d: '7 dagen',
  lb_period_30d: '30 dagen',
  lb_period_all: 'Altijd',
  lb_group_label: 'Categorie',
  lb_group_all: 'Alles',
  lb_group_ask: 'Vragen',
  lb_group_rate: 'Beoordelen',
  lb_group_confession: 'Bekentenis',
  lb_group_guesswho: 'Wie is het?',
  lb_group_message: 'Berichten',
  lb_error: 'Ranglijst kon niet worden geladen',
  lb_empty: 'Nog niemand in de ranglijst',
  lb_anon: 'Anoniem',
  lb_load_more: 'Meer laden',
  lb_questions: (n) => plural('nl', n, { one: '{n} vraag', other: '{n} vragen' }),
  lb_referrals: (n) => plural('nl', n, { one: '{n} ref.', other: '{n} refs.' }),

  // REWARDS SCREEN
  rw_title: 'Mijn beloningen',
  rw_error: 'Beloningen konden niet worden geladen',
  rw_level: (n) => `Level ${n}`,
  rw_confirmed_referrals: (n) =>
    plural('nl', n, { one: '{n} bevestigde verwijzing', other: '{n} bevestigde verwijzingen' }),
  rw_next_level_section: (n) =>
    plural('nl', n, { one: 'VOLGEND LEVEL — {n} verwijzing', other: 'VOLGEND LEVEL — {n} verwijzingen' }),
  rw_to_level: (level, target) => `naar level ${level}: ${target}`,
  rw_max_level: 'Maximaal level bereikt!',
  rw_badges_section: 'BADGES',
  rw_formats_section: 'BOX-FORMATEN',

  // QUESTION CARD
  qcard_status_new: 'Nieuw',
  qcard_status_hidden: 'Verborgen',
  qcard_status_answered: 'Beantwoord',
  qcard_moderation_flagged: 'Let op: verdacht bericht',
  qcard_answer_btn: 'Beantwoorden',
  qcard_answer_send: 'Versturen',
  qcard_share_btn: 'Delen',
  qcard_hide_btn: 'Verbergen',
  qcard_show_btn: 'Tonen',
  qcard_report_btn: 'Melden',
  qcard_block_btn: 'Blokkeren',
  qcard_unblock_btn: 'Deblokkeren',
  qcard_report_reason_label: 'Selecteer reden van melding:',
  qcard_reason_spam: 'Spam',
  qcard_reason_harassment: 'Pesten',
  qcard_reason_sexual: 'Seksuele inhoud',
  qcard_reason_other: 'Anders',
  qcard_error_hide: 'Zichtbaarheid kon niet worden gewijzigd.',
  qcard_error_report: 'Melding kon niet worden verzonden.',
  qcard_error_block: 'Afzender kon niet worden geblokkeerd.',
  qcard_report_done: 'Melding verzonden — bedankt.',
  qcard_block_done: 'Afzender geblokkeerd.',
  qcard_unblock_done: 'Afzender gedeblokkeerd.',

  // ERRORS (common humanized error messages)
  err_network: 'Geen verbinding met de server. Controleer je internet en probeer opnieuw.',
  err_server_unavailable: 'Server is tijdelijk niet beschikbaar, probeer het later opnieuw.',
  err_internal: 'Er ging iets mis bij ons. We kijken er al naar — probeer het later opnieuw.',
  err_bad_request: 'Controleer de ingevoerde gegevens.',
  err_generic: 'Actie kon niet worden voltooid, probeer het later opnieuw.',
  err_schema: 'Datafout. Probeer de app te updaten.',
  share_story_link_premium_note:
    'Een klikbare link in Stories vereist Telegram Premium. Zonder Premium kunnen je vrienden je toch vinden — je gebruikersnaam staat op de kaart.',

  // AUTH ERROR SCREEN
  auth_error_title: 'Toegangsfout',
  auth_error_desc:
    'Authenticatie mislukt. Sluit de Mini App en open hem opnieuw via Telegram.',

  // REWARD TOAST
  reward_toast_title: 'Nieuwe beloning!',
  reward_toast_close: '×',

  // BOTTOM TABBAR
  tab_questions: 'Vragen',
  tab_leaderboard: 'Ranglijst',
  tab_rewards: 'Beloningen',
  tab_settings: 'Instellingen',

  // SETTINGS SCREEN
  settings_title: 'Instellingen',
  settings_section_general: 'Algemeen',
  settings_notifications: 'Meldingen voor nieuwe vragen',
  settings_notifications_hint: 'De bot stuurt je een bericht als iemand een vraag stelt.',
  settings_notifications_error: 'Meldingeninstelling kon niet worden opgeslagen.',
  settings_language: 'Taal',
  settings_language_error: 'Taal kon niet worden gewijzigd. Probeer het opnieuw.',
  settings_section_about: 'Over de app',
  settings_version: 'Versie',

  // CODE ENTRY (work-020)
  code_modal_title: 'Story-links zijn alleen voor Premium',
  code_modal_explain:
    'Telegram geeft alleen Premium-abonnees een klikbare story-link. Voeg in plaats daarvan je code toe aan de story — kijkers typen hem in de app in en stellen je een anonieme vraag.',
  code_modal_your_code: 'Jouw code',
  code_modal_copy: 'Code kopiëren',
  code_modal_copied: 'Gekopieerd',
  code_modal_share_btn: 'Delen via story',
  code_modal_loading: 'Code ophalen…',
  code_modal_error: 'Code kon niet worden opgehaald. Probeer het later.',
  ask_code_title: 'Voer een code in',
  ask_code_desc:
    'Via een story gekomen? Zoek de code erin (bijv. 1234) en typ hem hieronder.',
  ask_code_placeholder: 'Code uit de story',
  ask_code_find: 'Zoeken',
  ask_code_not_found: 'Code niet gevonden. Controleer de cijfers en probeer opnieuw.',
  ask_code_invalid: 'De code bestaat uit 4 cijfers.',
  code_onboarding_header: 'Via een story gekomen?',
  code_onboarding_text: 'Voer de code van een vriend in om hem anoniem een vraag te stellen.',
  code_onboarding_action: 'Code invoeren',
  settings_ask_by_code: 'Vragen via code',
  settings_my_code_title: 'Jouw story-code',
  settings_my_code_hint:
    'Zet deze code in je story — kijkers typen hem in om je een anonieme vraag te stellen. Telegram geeft alleen Premium-abonnees klikbare story-links.',
  settings_my_code_rotate: 'Nieuwe code ophalen',
  settings_my_code_rotating: 'Bijwerken…',
  settings_my_code_done: 'Code bijgewerkt',
  settings_my_code_error: 'Code kon niet worden opgehaald.',
  settings_my_code_rotate_confirm_title: 'Code wijzigen?',
  settings_my_code_rotate_confirm_desc:
    'Je oude code werkt meteen niet meer. Iedereen die \'m in je story zag, kan je dan niet meer bereiken. Wijzig \'m alleen als iemand je spamt.',
  settings_my_code_rotate_confirm_action: 'Code wijzigen',

  // BOT
  bot_greeting: (name) => (name ? `Hoi, ${name}! 👋` : 'Hoi! 👋'),
  bot_onboarding:
    'Dit is VeylMe — anonieme vragen.\n\n' +
    'Open de Mini App, plaats een kaart «Stel me anoniem een vraag» in je Story — ' +
    'en ontvang anonieme vragen hier.\n\n' +
    '⚠️ 18+. Wees respectvol: pesten, bedreigingen en verboden inhoud ' +
    'zijn niet toegestaan. Anonimiteit ≠ straffeloosheid — overtredingen gaan naar moderatie.',
  bot_open_btn: 'VeylMe openen',
  notify_answer_btn: 'Beantwoorden',
  bot_invite_text:
    'Iemand heeft een VeylMe-link gedeeld.\n\n' +
    'Open de Mini App om een anonieme vraag te stellen:',
  bot_invite_btn: 'Anoniem vragen',

  // WORK-027 i18n sweep
  tagline: 'Stel me anoniem een vraag',
  share_ask_action: 'Vragen',
  share_answered_text: 'Ik heb een anonieme vraag beantwoord 👀',
  err_not_in_telegram: 'De Mini App draait niet in Telegram. Start de app opnieuw.',
  badge_viral_starter: 'Virale start',
  badge_storyteller: 'Verhalenverteller',
  badge_loop_master: 'Loop-meester',
  theme_confession: 'Bekentenissen',
  theme_guesswho: 'Wie is het',
  rw_format_named: (name) => `formaat «${name}»`,
  card_label_question: 'Vraag',
  card_label_answer: 'Antwoord',
  card_invite_subtitle: 'anoniem, geen aanmelding nodig',
  card_code_hint: 'VOER DE CODE IN DE APP IN',
  share_msg_title: (brand) => `${brand} · Anonieme vragen`,
  notify_new_question: '🔔 Iemand heeft je een anonieme vraag gesteld',
  notify_unanswered_reminder: (n) =>
    plural('nl', n, { one: '🔔 Je hebt {n} onbeantwoorde vraag', other: '🔔 Je hebt {n} onbeantwoorde vragen' }),
  notify_ban_permanent: 'permanent',
  notify_ban_until: (date) => `tot ${date}`,
  notify_ban_msg: (untilPhrase) =>
    `⛔ Je bent ${untilPhrase} geblokkeerd in VeylMe. Te veel ontvangers hebben je berichten gemeld.\nAls je denkt dat dit een vergissing is, kun je bezwaar indienen in de app.`,
  notify_warning_msg:
    '⚠️ Meerdere ontvangers hebben je berichten gemeld. Nog een paar meer en je wordt geblokkeerd in VeylMe.',

  // MY QUESTIONS SCREEN + InvitePanel non-premium hint (retention-answer-loop)
  my_questions_title: 'Mijn vragen',
  my_questions_empty_title: 'Je hebt nog geen vraag gesteld',
  my_questions_empty_desc: 'Stel een vraag in iemands mailbox — het antwoord verschijnt hier.',
  my_questions_loading: 'Controleren op antwoord…',
  my_questions_waiting: 'Wacht op antwoord',
  my_questions_answer_label: 'Antwoord',
  settings_my_questions: 'Mijn vragen',
  ask_sent_find_reply_hint: 'Je vindt het antwoord later terug bij «Mijn vragen».',
  inbox_story_nonpremium_hint: 'Een Story-link is niet klikbaar zonder Telegram Premium, daarom tonen we een code — delen in een chat is betrouwbaarder.',

  // SESSION EXPIRY SCREEN (work-038)
  session_expired_title: 'Je Telegram-toegang is verlopen',
  session_expired_desc: 'Telegram geeft de app maar één keer toegang, bij het opstarten, en die is al verlopen. Open de app opnieuw — de tekst die je begon hebben we bewaard.',
  session_expired_desc_no_storage: 'Je Telegram-toegang is verlopen en de app moet opnieuw worden geopend. We konden de tekst die je typte niet bewaren — kopieer hem voordat je weggaat.',
  session_expired_reopen: 'Opnieuw openen',
  session_expired_stuck_title: 'Telegram gaf dezelfde oude toegang terug',
  session_expired_stuck_desc: 'Sluit de app volledig via het botmenu en open hem opnieuw — dan geeft Telegram nieuwe toegang.',
  session_auth_failed_title: 'Kon je aanmelding niet bevestigen',
  session_auth_failed_desc: 'Telegram heeft je aanmelding in de app niet bevestigd. Sluit de app en open hem opnieuw vanuit de chat met de bot.',
  session_expired_manual_close: 'De app kan zichzelf hier niet sluiten — sluit hem zelf en open hem opnieuw vanuit de chat met de bot.',
  session_expiring_soon: 'Je Telegram-toegang verloopt binnenkort. Open de app opnieuw zodat er niets verloren gaat.',
  my_questions_check_failed: 'Kon niet controleren op antwoorden. Probeer het later opnieuw.',
  settings_notifications_load_error: 'Kon deze instelling niet laden.',
};
